import { useTranslation } from 'react-i18next';
import { humanizeApiError } from '@/lib/humanizeApiError';

/**
 * "Couldn't load — try again" state for a panel or list.
 *
 * Exists because several surfaces had no error branch at all: a failed query
 * fell through to the EMPTY state, so a moderator saw "Жалоб нет" on a broken
 * request and walked away, and a failed roster fetch left four grey bars
 * forever. Showing the reason plus a retry is the difference between "nothing
 * here" and "this didn't load".
 *
 * Message comes from the shared humanizeApiError map (same wording the toasts
 * use), so server codes never leak into the UI.
 */
export function LoadError(props: {
  error: unknown;
  onRetry: () => void;
  /** Fallback title when the error isn't one we have a mapping for. */
  fallback?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const humanized = humanizeApiError(props.error, props.fallback ?? t('common.loadFailed'));

  return (
    <div
      role="alert"
      className={'flex flex-col items-center gap-2 px-4 py-10 text-center ' + (props.className ?? '')}
    >
      <p className="text-sm leading-relaxed text-ink-muted">
        {humanized.title}
        {humanized.hint ? ` ${humanized.hint}` : ''}
      </p>
      <button
        type="button"
        onClick={props.onRetry}
        className="inline-flex min-h-11 items-center rounded-lg px-4 text-sm font-medium text-accent transition-colors hover:bg-accent/8 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {t('common.retry')}
      </button>
    </div>
  );
}
